import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axiosConfig";
import { Box, CircularProgress, Typography, Button } from "@mui/material";
import { ArrowBack, Download } from "@mui/icons-material";
import "./MonthlyReport.css";

const METRIC_FIELDS = {
  telephoneSalesCalls: "Telephone",
  faceToFace: "Face to Face",
  appointmentMade: "Appt Made",
  appointmentKept: "Appt Kept",
  factFind: "Fact Find",
  serviceReviews: "Service Reviews",
  socialCalls: "Social Calls",
  highIncomeWealthy: "HIW",
  referrals: "Referrals",
  closingInterview: "Closing Int.",
  salesClose: "Sales Close",
};

const fieldKeys = Object.keys(METRIC_FIELDS);

const formatDay = (day) => {
  if (day > 3 && day < 21) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

const AdminMonthlyReport = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [reportData, setReportData] = useState([]);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!userId) {
      setError(
        "Invalid user ID. Please access this page from the Performance Statistics page."
      );
      setIsLoading(false);
      return;
    }

    const fetchReport = async () => {
      setIsLoading(true);
      setError("");
      const month = selectedDate.getMonth() + 1;
      const year = selectedDate.getFullYear();

      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        };
        const response = await api.get(
          `/api/admin/monthly-report/${userId}?month=${month}&year=${year}`,
          { headers }
        );
        setReportData(response.data.entries);
        setUser(response.data.user);
      } catch (err) {
        console.error("Failed to fetch monthly report:", err);
        setError("Could not load report data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [selectedDate, userId]);

  const handleMonthChange = (e) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(parseInt(e.target.value, 10));
    setSelectedDate(newDate);
  };

  const handleYearChange = (e) => {
    const newDate = new Date(selectedDate);
    newDate.setFullYear(parseInt(e.target.value, 10));
    setSelectedDate(newDate);
  };

  const daysInMonth = useMemo(() => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    return new Date(year, month + 1, 0).getDate();
  }, [selectedDate]);

  const monthlyTotals = useMemo(() => {
    const totals = { totalPoints: 0 };
    // Initialize totals for all metric fields
    fieldKeys.forEach((key) => {
      totals[key] = 0;
    });

    reportData.forEach((entry) => {
      totals.totalPoints += Number(entry.points) || 0;
      // Sum up the metric fields
      fieldKeys.forEach((key) => {
        if (entry.hasOwnProperty(key)) {
          totals[key] += Number(entry[key]) || 0;
        }
      });
    });
    return totals;
  }, [reportData]);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const handleBack = () => {
    navigate("/performance-statistics");
  };

  const handleDownloadClick = () => {
    downloadCSV();
  };

  const downloadCSV = () => {
    const headers = [
      "Date",
      ...fieldKeys.map((key) => METRIC_FIELDS[key]),
      "Daily Total Points",
    ];
    const rows = [];

    // Add user info
    rows.push([`Name: ${user.name}`]);
    rows.push([`Role: ${user.role.replace("_", " ")}`]);
    rows.push([`Location: ${user.location || "N/A"}`]);
    rows.push([
      `Month: ${
        monthNames[selectedDate.getMonth()]
      } ${selectedDate.getFullYear()}`,
    ]);
    rows.push([]); // Empty row

    // Add table headers
    rows.push(headers);

    // Add data rows
    Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      const dateStr = `${selectedDate.getFullYear()}-${String(
        selectedDate.getMonth() + 1
      ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const entry = reportData.find((e) => {
        const entryDate = new Date(e.entry_date);
        const entryDateStr = entryDate.toISOString().split("T")[0];
        return entryDateStr === dateStr;
      });

      const row = [formatDay(day)];
      fieldKeys.forEach((key) => {
        row.push(entry ? entry[key] || 0 : 0);
      });
      row.push(entry ? entry.points || 0 : 0);
      rows.push(row);
    });

    // Add totals row
    const totalsRow = ["Monthly Total"];
    fieldKeys.forEach((key) => {
      totalsRow.push(monthlyTotals[key]);
    });
    totalsRow.push(monthlyTotals.totalPoints);
    rows.push(totalsRow);

    // Convert to CSV
    const csvContent = rows
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Monthly_Report_${user.name}_${
        monthNames[selectedDate.getMonth()]
      }_${selectedDate.getFullYear()}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="monthly-report-container">
      <div className="report-header">
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Performance Statistics
        </Button>
        {user && (
          <div className="user-info">
            <h2>Monthly Performance Report</h2>
            <p>
              <strong>Name:</strong> {user.name}
            </p>
            <p>
              <strong>Role:</strong> {user.role.replace("_", " ")}
            </p>
            <p>
              <strong>Location:</strong> {user.location || "N/A"}
            </p>
          </div>
        )}
        <div className="report-filters">
          <select value={selectedDate.getMonth()} onChange={handleMonthChange}>
            {monthNames.map((name, index) => (
              <option key={name} value={index}>
                {name}
              </option>
            ))}
          </select>
          <select
            value={selectedDate.getFullYear()}
            onChange={handleYearChange}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ textAlign: "center", p: 4 }}>
          {error}
        </Typography>
      ) : (
        <>
          <div className="report-table-wrapper">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Date</th>
                  {fieldKeys.map((key) => (
                    <th key={key}>{METRIC_FIELDS[key]}</th>
                  ))}
                  <th>Daily Total Points</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const dateStr = `${selectedDate.getFullYear()}-${String(
                    selectedDate.getMonth() + 1
                  ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const entry = reportData.find((e) => {
                    const entryDate = new Date(e.entry_date);
                    const entryDateStr = entryDate.toISOString().split("T")[0];
                    return entryDateStr === dateStr;
                  });

                  return (
                    <tr key={day}>
                      <td>{formatDay(day)}</td>
                      {fieldKeys.map((key) => (
                        <td key={key}>{entry ? entry[key] || 0 : 0}</td>
                      ))}
                      <td>{entry ? entry.points || 0 : 0}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td>Monthly Total</td>
                  {fieldKeys.map((key) => (
                    <td key={key}>{monthlyTotals[key]}</td>
                  ))}
                  <td>{monthlyTotals.totalPoints}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleDownloadClick}
              sx={{ minWidth: 200 }}
            >
              Download Report
            </Button>
          </Box>
        </>
      )}
    </div>
  );
};

export default AdminMonthlyReport;
